import React, { useMemo, useState } from 'react';

import DashboardTemplate from 'src/templates/DashboardTemplate';
import ModulesList from 'src/pages/store/components/ModulesList';
import ModuleInfo from 'src/pages/store/components/ModuleInfo';
import { ModuleView } from 'src/interfaces/entities/module';
import ObserversList from 'src/pages/store/components/ObserversList';
import { useGetModulesQuery } from 'src/redux/services/centralApi';
import { Flex, Input, Select, Button } from 'antd';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';

export default function StorePage() {
	const { data: modulesData, } = useGetModulesQuery({});

	const [ selectedObservers, setSelectedObservers, ] = useState<Array<string>>([]);
	const [ module, setModule, ] = useState<ModuleView | null>(null);

	const [ searchGlobal, setSearchGlobal, ] = useState<string>('');
	const [ targetSearch, setTargetSearch, ] = useState<string>();

	const modulesList = useMemo(() => {
		const activeModules = modulesData?.filter(module => 
			module.is_active === true
		);

		if (!searchGlobal && !targetSearch) return activeModules;

		const filteredModules = activeModules?.filter(el => {
			const matchesTarget = targetSearch ? el.target === targetSearch : true;
			
			if (!searchGlobal) return matchesTarget;
			
			const matchesModuleName = el.name.toLowerCase().includes(searchGlobal.toLowerCase());
			
			const matchesObserverName = el.observers?.some(observer => 
				observer.name.toLowerCase().includes(searchGlobal.toLowerCase())
			) || false;
			
			const matchesGlobalSearch = matchesModuleName || matchesObserverName;
			
			return matchesGlobalSearch && matchesTarget;
		});

		return filteredModules;
	}, [ modulesData, searchGlobal, targetSearch, ]);

	const targetOptions = useMemo(() => {
		if(!modulesData) return [];

		const targetList: string[] = [];
		modulesData.forEach(module => {
			if(!targetList.includes(module.target)){
				targetList.push(module.target);
			}
		});

		return targetList.map(target => ({
			value: target,
			label: target,
		})); 
	}, [ modulesData, ]);

	const observersInList = useMemo(() => {
		if(!modulesData) return [];

		let observers;
		if(module) {
			observers = module.observers || [];
		} else {
			observers = modulesData.flatMap(module => {
				if(!module.observers || !module.is_active) return [];
				return module.observers;
			});
		}

		if(searchGlobal && searchGlobal.trim() !== '') {
			observers = observers.filter(observer => 
				observer.name.toLowerCase().includes(searchGlobal.toLowerCase())
			);
		}

		return observers;
	}, [ module, modulesData, searchGlobal, ]);

	const handleSelectAllObservers = () => {
		const allObserversIds = observersInList.map(observer => observer.observer_id);
		const areAllSelected = allObserversIds.every(id => selectedObservers.includes(id));
		
		if (areAllSelected) {
			setSelectedObservers([]);
		} else {
			setSelectedObservers(allObserversIds);
		}
	};

	const areAllObserversSelected = useMemo(() => {
		if (observersInList.length === 0) return false;
		const allObserversIds = observersInList.map(observer => observer.observer_id);
		return allObserversIds.every(id => selectedObservers.includes(id));
	}, [ observersInList, selectedObservers, ]);
	
	return (
		<DashboardTemplate>
			<Flex vertical gap={8}>
				<Flex gap={8}>
					<Input
						onChange={(e) => setSearchGlobal(e.target.value)}
						placeholder='Pesquisar módulos e observers...'
					/>
					<Select
						allowClear
						options={targetOptions}
						onChange={(value) => setTargetSearch(value)}
						placeholder='Filtrar ERP'
					/>
				</Flex>
				<ModulesList
					modules={modulesList}
					selectedModule={module}
					onModuleSelect={(value) => {
						setModule(value);
						setSelectedObservers([]);
					}}
				/>
			</Flex>
			<hr />
			{
				module &&
				<ModuleInfo 
					selectedObservers={selectedObservers} 
					module={module} 
					hasSelectedObservers={selectedObservers.length > 0}
				/>
			}
			{module && observersInList.length > 0 && (
				<Flex justify="end" style={{ marginBottom: 8, }}>
					<Button 
						onClick={handleSelectAllObservers}
						type="primary"
						icon={areAllObserversSelected ? <MinusOutlined /> : <CheckOutlined />}
					>
						{areAllObserversSelected ? 'Desmarcar tudo' : 'Selecionar tudo'}
					</Button>
				</Flex>
			)}
			<ObserversList
				module={Boolean(module)}
				observers={observersInList}
				selectedObservers={selectedObservers}
				setSelectedObservers={setSelectedObservers}
			/>
		</DashboardTemplate>
	);
}
